import { useCallback, useEffect, useRef } from "react";
import {
  type CashierPaymentMethod,
  type CashierPaymentMethodGroup,
  type CashierTradingAccount,
  type CurrencyPrimitive,
  getExchangeRate,
} from "@/api/cashier";
import { useAccount } from "@/hooks/deposit/useAccount";
import { useUserData } from "@/hooks/useUserData";
import { moneyToMajorUnits, toMinorUnits } from "@/utils/helpers";
import { useAmountForm } from "./useAmountForm";
import { useAmountValidator } from "./useAmountValidator";
import { useCurrencies } from "./useCurrencies";
import { useMethod } from "./useMethod";

type TConvert = {
  processingCurrency: CurrencyPrimitive;
  paymentMethod: {
    method?: CashierPaymentMethod;
    group?: CashierPaymentMethodGroup;
  };
  amount?: number;
  // if processing -> trading is a reverse exchange
  isReverse?: boolean;
  selectedAccount: CashierTradingAccount | undefined;
  selectedPaymentMethod: CashierPaymentMethod | undefined;
};

// FIXME this should be refactored
export const useCurrencyConverter = () => {
  const { data: userData } = useUserData();
  const { selectedAccount } = useAccount();
  const {
    setSelectedPaymentMethod,
    setSelectedPaymentMethodGroup,
    selectedPaymentMethod,
  } = useMethod();

  const { updateAmountFormData } = useAmountForm();
  const { selectedProcessingCurrency } = useCurrencies();
  const amountValidator = useAmountValidator();

  const abortCtrl = useRef(new AbortController());
  const requestIdRef = useRef(0);

  useEffect(() => {
    abortCtrl.current = new AbortController();
    return () => abortCtrl.current.abort(new Error("Component unmounted"));
  }, []);

  const cancelExistingCall = useCallback(() => {
    abortCtrl.current.abort(new Error("Request superseded by newer request"));
    abortCtrl.current = new AbortController();
    // Invalidate any in-flight request ID so a concurrent caller (e.g. the user
    // typing a new amount while the prefill API is still pending) can pre-emptively
    // mark that response as stale before the debounce fires.
    requestIdRef.current++;
  }, []);

  /**
   * Convert the amount from processing to trading currency
   * @param processingCurrency - the currency to convert from
   * @param paymentMethod - the payment method to use for the conversion
   * @param amount - the amount to convert
   * @param isReverse - if processing -> trading is a reverse exchange
   * @returns isSuccess - if the conversion was successful
   */
  const handlePaymentMethodSetWithConversion = useCallback(
    async ({
      processingCurrency,
      paymentMethod,
      amount,
      isReverse = false,
      selectedAccount,
      selectedPaymentMethod: _selectedPaymentMethod,
    }: TConvert) => {
      if (
        !selectedAccount ||
        !paymentMethod.method ||
        !selectedAccount ||
        !userData
      ) {
        return {
          isSuccess: false,
          isCancelled: false,
          newTradingAmount: 0,
          newProcessingAmount: 0,
        };
      }

      updateAmountFormData({
        amountIsLoading: true,
      });

      cancelExistingCall();
      const currentRequestId = requestIdRef.current;

      const amountCurrencyDecimals = isReverse
        ? processingCurrency.decimals
        : selectedAccount.currency.decimals;

      const minorAmount = toMinorUnits(amount || 0, amountCurrencyDecimals);

      const response = await getExchangeRate({
        query: {
          "from-currency-code": isReverse
            ? processingCurrency.code
            : selectedAccount?.currency.code,
          "to-currency-code": isReverse
            ? selectedAccount?.currency.code
            : processingCurrency.code,
          amount: String(minorAmount),
          "country-code": userData.country_code,
          "license-id": userData.license_id,
          "payment-method-id": paymentMethod.method.id,
          "is-reverse": isReverse,
        },
        signal: abortCtrl.current.signal,
      });

      if (currentRequestId !== requestIdRef.current) {
        // Superseded by a newer conversion request (e.g. rapid payment-method
        // switching) - not a real failure, so callers should ignore it rather
        // than surfacing an exchange-rate error.
        return {
          isSuccess: false,
          isCancelled: true,
          newTradingAmount: "",
          newProcessingAmount: "",
        };
      }

      if (response.error) {
        updateAmountFormData({ amountIsLoading: false });
        return {
          isSuccess: false,
          isCancelled: false,
          newTradingAmount: "",
          newProcessingAmount: "",
        };
      }

      const data = response.data;

      const rateNumber = Number(data.rate);
      let reversedRate = rateNumber;

      if (!isReverse) {
        // need to reverse the rate because we want to always show 1 processing currency = x trading currency
        // if we are converting from trading to processing, we need to reverse the rate
        // if it necessary we can start a new request to get the reverse rate
        reversedRate = 1 / rateNumber;
      }

      const toCurrencyNumber = moneyToMajorUnits(data.to_money) ?? 0;
      const fromCurrencyNumber = moneyToMajorUnits(data.from_money) ?? 0;

      const tradingValue = isReverse
        ? toCurrencyNumber > 0
          ? toCurrencyNumber
          : 0
        : fromCurrencyNumber > 0
          ? fromCurrencyNumber
          : 0;
      const processingValue = isReverse
        ? fromCurrencyNumber > 0
          ? fromCurrencyNumber
          : 0
        : toCurrencyNumber > 0
          ? toCurrencyNumber
          : 0;

      updateAmountFormData({
        tradingValue,
        processingValue,
        selectedProcessingCurrency: processingCurrency,
        externalProcessingCurrency: processingCurrency,
        externalTradingAmount: tradingValue,
        externalProcessingAmount: processingValue,
        amountIsLoading: false,
        rate: reversedRate,
        validationError: tradingValue
          ? amountValidator(
              tradingValue,
              processingCurrency,
              paymentMethod.method
            )
          : null,
      });

      setSelectedPaymentMethod(paymentMethod.method);
      setSelectedPaymentMethodGroup(paymentMethod.group);

      return { isSuccess: true, isCancelled: false };
    },
    [
      setSelectedPaymentMethod,
      setSelectedPaymentMethodGroup,
      updateAmountFormData,
      amountValidator,
      cancelExistingCall,
      userData,
    ]
  );

  const refetchRate = useCallback(async () => {
    if (
      !selectedAccount ||
      !selectedPaymentMethod ||
      !selectedProcessingCurrency ||
      !userData
    ) {
      return;
    }

    cancelExistingCall();

    const response = await getExchangeRate({
      query: {
        "from-currency-code": selectedProcessingCurrency.code,
        "to-currency-code": selectedAccount.currency.code,
        amount: "0",
        "country-code": userData.country_code,
        "license-id": userData.license_id,
        "payment-method-id": selectedPaymentMethod.id,
        "is-reverse": true,
      },
      signal: abortCtrl.current.signal,
    });

    if (response.error) {
      return;
    }

    const data = response.data;

    updateAmountFormData({
      rate: data.rate,
    });
  }, [
    selectedAccount,
    selectedPaymentMethod,
    selectedProcessingCurrency,
    updateAmountFormData,
    cancelExistingCall,
    userData,
  ]);

  const handleCurrencyChange = useCallback(
    async (newProcessingCurrency: CurrencyPrimitive) => {
      if (!selectedAccount || !selectedPaymentMethod || !userData)
        return {
          isSuccess: false,
          newTradingAmount: 0,
          newProcessingAmount: 0,
        };

      cancelExistingCall();

      const response = await getExchangeRate({
        query: {
          "from-currency-code": newProcessingCurrency.code,
          "to-currency-code": selectedAccount?.currency.code,
          amount: "0",
          "country-code": userData.country_code,
          "license-id": userData.license_id,
          "payment-method-id": selectedPaymentMethod.id,
          "is-reverse": true,
        },
        signal: abortCtrl.current.signal,
      });

      if (response.error) {
        return {
          isSuccess: false,
          newTradingAmount: 0,
          newProcessingAmount: 0,
        };
      }

      const data = response.data;
      updateAmountFormData({
        tradingValue: 0,
        processingValue: 0,
        selectedProcessingCurrency: newProcessingCurrency,
        externalProcessingCurrency: newProcessingCurrency,
        externalTradingAmount: 0,
        externalProcessingAmount: 0,
        amountIsLoading: false,
        rate: data.rate,
        validationError: null,
      });
      return { isSuccess: true, newProcessingAmount: 0, newTradingAmount: 0 };
    },
    [
      selectedAccount,
      selectedPaymentMethod,
      updateAmountFormData,
      cancelExistingCall,
      userData,
    ]
  );

  const handleAmountChange = useCallback(
    async (amount: number, isReverse: boolean) => {
      if (
        !selectedProcessingCurrency ||
        !selectedAccount ||
        !selectedPaymentMethod ||
        !userData
      )
        return {
          isSuccess: false,
          newTradingAmount: 0,
          newProcessingAmount: 0,
        };

      cancelExistingCall();
      const currentRequestId = requestIdRef.current;

      updateAmountFormData({
        amountIsLoading: true,
      });

      // The typed amount is denominated in the exchange's "from" currency (the
      // trading account for a normal exchange, the processing currency for a
      // reverse one). The backend reads the minor amount using that currency's
      // decimals, so scale by the from-currency's decimals here.
      const amountCurrencyDecimals = isReverse
        ? selectedProcessingCurrency.decimals
        : selectedAccount.currency.decimals;

      const minorAmount = toMinorUnits(amount || 0, amountCurrencyDecimals);

      const response = await getExchangeRate({
        query: {
          "from-currency-code": isReverse
            ? selectedProcessingCurrency?.code
            : selectedAccount?.currency.code,
          "to-currency-code": isReverse
            ? selectedAccount?.currency.code
            : selectedProcessingCurrency?.code,
          amount: String(minorAmount),
          "country-code": userData.country_code,
          "license-id": userData.license_id,
          "payment-method-id": selectedPaymentMethod.id,
          "is-reverse": isReverse,
        },
        signal: abortCtrl.current.signal,
      });

      if (currentRequestId !== requestIdRef.current) {
        return {
          isSuccess: false,
          newTradingAmount: 0,
          newProcessingAmount: 0,
        };
      }

      if (response.error) {
        updateAmountFormData({
          amountIsLoading: false,
          amountInputPending: false,
        });
        return {
          isSuccess: false,
          newTradingAmount: 0,
          newProcessingAmount: 0,
        };
      }

      const data = response.data;
      const rateNumber = Number(data.rate);

      let reversedRate = rateNumber;

      if (!isReverse) {
        // need to reverse the rate because we want to always show 1 processing currency = x trading currency
        // if we are converting from trading to processing, we need to reverse the rate
        // if it necessary we can start a new request to get the reverse rate
        reversedRate = 1 / rateNumber;
      }

      const toCurrencyNumber = moneyToMajorUnits(data.to_money) ?? 0;
      const fromCurrencyNumber = moneyToMajorUnits(data.from_money) ?? 0;

      const tradingValue = isReverse
        ? toCurrencyNumber > 0
          ? toCurrencyNumber
          : 0
        : fromCurrencyNumber > 0
          ? fromCurrencyNumber
          : 0;
      const processingValue = isReverse
        ? fromCurrencyNumber > 0
          ? fromCurrencyNumber
          : 0
        : toCurrencyNumber > 0
          ? toCurrencyNumber
          : 0;

      updateAmountFormData({
        tradingValue,
        processingValue,
        selectedProcessingCurrency: selectedProcessingCurrency,
        externalProcessingCurrency: selectedProcessingCurrency,
        externalTradingAmount: tradingValue,
        externalProcessingAmount: processingValue,
        amountIsLoading: false,
        amountInputPending: false,
        rate: reversedRate,
        validationError:
          tradingValue > 0
            ? amountValidator(
                tradingValue,
                selectedProcessingCurrency,
                selectedPaymentMethod
              )
            : null,
      });
      return {
        isSuccess: true,
        newProcessingAmount: processingValue,
        newTradingAmount: tradingValue,
      };
    },
    [
      selectedAccount,
      selectedPaymentMethod,
      selectedProcessingCurrency,
      updateAmountFormData,
      amountValidator,
      cancelExistingCall,
      userData,
    ]
  );

  return {
    handlePaymentMethodSetWithConversion,
    handleCurrencyChange,
    handleAmountChange,
    refetchRate,
    cancelPendingConversion: cancelExistingCall,
  };
};
